/**
 * @playtable/cart-optimizer — Standalone Multi-Vendor MTG Deck Cart & Shipping Optimizer
 *
 * Designed as an independent zero-dependency TypeScript package shared between:
 *  - Playtable ("Buy Deck / Cheapest Cart" modal & REST endpoints)
 *  - Deckstr.gg (`MarketTab` compatible input/output DTOs without modifying `deckstr.gg`)
 */

export type VendorId =
  | 'tcgplayer_direct'
  | 'tcgplayer_market'
  | 'card_kingdom'
  | 'coolstuffinc'
  | 'starcitygames'
  | 'cardmarket';

export type CardCondition = 'NM' | 'LP' | 'MP' | 'HP';

export type OptimizationMode = 'cheapest_total' | 'balanced' | 'fewest_packages';

export type PrintingPreference = 'cheapest_printing' | 'exact_printing';

export type FinishPreference = 'nonfoil' | 'foil' | 'any';

export interface VendorShippingSchedule {
  vendorId: VendorId;
  vendorLabel: string;
  region: 'US' | 'EU';
  baseShippingUsd: number;
  perCardShippingUsd: number;
  freeShippingThresholdUsd: number | null;
  checkoutUrl: string;
}

export const VENDOR_SHIPPING_SCHEDULES: Record<VendorId, VendorShippingSchedule> = {
  tcgplayer_direct: {
    vendorId: 'tcgplayer_direct',
    vendorLabel: 'TCGplayer Direct',
    region: 'US',
    baseShippingUsd: 3.99,
    perCardShippingUsd: 0.0,
    freeShippingThresholdUsd: 50.0,
    checkoutUrl: 'https://www.tcgplayer.com/massentry',
  },
  tcgplayer_market: {
    vendorId: 'tcgplayer_market',
    vendorLabel: 'TCGplayer Marketplace',
    region: 'US',
    baseShippingUsd: 1.99,
    perCardShippingUsd: 0.0,
    freeShippingThresholdUsd: 35.0,
    checkoutUrl: 'https://www.tcgplayer.com/massentry',
  },
  card_kingdom: {
    vendorId: 'card_kingdom',
    vendorLabel: 'Card Kingdom',
    region: 'US',
    baseShippingUsd: 4.99,
    perCardShippingUsd: 0.0,
    freeShippingThresholdUsd: 75.0,
    checkoutUrl: 'https://www.cardkingdom.com/builder',
  },
  coolstuffinc: {
    vendorId: 'coolstuffinc',
    vendorLabel: 'CoolStuffInc',
    region: 'US',
    baseShippingUsd: 1.99,
    perCardShippingUsd: 0.08,
    freeShippingThresholdUsd: 50.0,
    checkoutUrl: 'https://www.coolstuffinc.com/main_deckBuilder.php',
  },
  starcitygames: {
    vendorId: 'starcitygames',
    vendorLabel: 'Star City Games',
    region: 'US',
    baseShippingUsd: 5.49,
    perCardShippingUsd: 0.0,
    freeShippingThresholdUsd: 100.0,
    checkoutUrl: 'https://starcitygames.com/deck-builder/',
  },
  cardmarket: {
    vendorId: 'cardmarket',
    vendorLabel: 'Cardmarket (EU)',
    region: 'EU',
    baseShippingUsd: 2.49,
    perCardShippingUsd: 0.12,
    freeShippingThresholdUsd: null,
    checkoutUrl: 'https://www.cardmarket.com/en/Magic/Wants',
  },
};

export const CONDITION_RANK: Record<CardCondition, number> = {
  NM: 4,
  LP: 3,
  MP: 2,
  HP: 1,
};

export const BASIC_LAND_NAMES = new Set([
  'plains',
  'island',
  'swamp',
  'mountain',
  'forest',
  'wastes',
  'snow-covered plains',
  'snow-covered island',
  'snow-covered swamp',
  'snow-covered mountain',
  'snow-covered forest',
]);

export interface CartDeckItem {
  cardName: string;
  quantity: number;
  setCode?: string;
  collectorNumber?: string;
  foil?: boolean;
  isBasicLand?: boolean;
}

export interface VendorQuote {
  quoteId: string;
  vendorId: VendorId;
  /** Distinct seller identifier (for marketplaces with independent sellers, each seller ships its own package) */
  sellerId: string;
  sellerName: string;
  cardName: string;
  setCode: string;
  collectorNumber: string;
  condition: CardCondition;
  foil: boolean;
  unitPriceUsd: number;
  stockQty: number;
  /** Optional seller-specific shipping override (e.g. micro marketplace sellers with $1.49 shipping and no free threshold) */
  sellerBaseShippingUsd?: number;
  sellerPerCardShippingUsd?: number;
  sellerFreeShippingThresholdUsd?: number | null;
}

export interface CartOptimizationPreferences {
  mode: OptimizationMode;
  printingPreference: PrintingPreference;
  minCondition: CardCondition;
  finishPreference: FinishPreference;
  excludeBasicLands?: boolean;
  allowedVendors?: VendorId[];
  /** Custom penalty per extra shipment in 'balanced' mode (defaults to $2.25) */
  balancedPackagePenaltyUsd?: number;
  /** When true under exact_printing, never fall back to another printing if exact setCode is unavailable */
  strictExactPrinting?: boolean;
}

export const DEFAULT_CART_PREFERENCES: CartOptimizationPreferences = {
  mode: 'cheapest_total',
  printingPreference: 'cheapest_printing',
  minCondition: 'LP',
  finishPreference: 'nonfoil',
  excludeBasicLands: true,
  allowedVendors: ['tcgplayer_direct', 'tcgplayer_market', 'card_kingdom', 'coolstuffinc', 'starcitygames'],
  balancedPackagePenaltyUsd: 2.25,
  strictExactPrinting: false,
};

export interface AllocatedCartLine {
  quoteId: string;
  cardName: string;
  setCode: string;
  collectorNumber: string;
  condition: CardCondition;
  foil: boolean;
  quantity: number;
  unitPriceUsd: number;
  lineTotalUsd: number;
  printingFallbackUsed?: boolean;
}

export interface VendorPackagePlan {
  sellerId: string;
  sellerName: string;
  vendorId: VendorId;
  vendorLabel: string;
  region: 'US' | 'EU';
  items: AllocatedCartLine[];
  totalCardQty: number;
  subtotalUsd: number;
  shippingUsd: number;
  freeShippingThresholdUsd: number | null;
  freeShippingUnlocked: boolean;
  amountToFreeShippingUsd: number | null;
  totalUsd: number;
  checkoutUrl: string;
  massEntryText: string;
  stepChecklist: string[];
}

export interface UnfulfilledCartItem {
  cardName: string;
  missingQty: number;
  reason: string;
}

export interface OptimizedDeckCartResult {
  mode: OptimizationMode;
  preferences: CartOptimizationPreferences;
  packages: VendorPackagePlan[];
  packageCount: number;
  cardsSubtotalUsd: number;
  shippingTotalUsd: number;
  grandTotalUsd: number;
  naivePerCardTotalUsd: number;
  naivePackageCount: number;
  bestSingleSellerTotalUsd: number | null;
  savingsVsNaiveUsd: number;
  savingsVsSingleSellerUsd: number;
  unfulfilled: UnfulfilledCartItem[];
  collectionSummary: {
    totalOriginalQty: number;
    excludedBasicsQty: number;
    subtractedOwnedQty: number;
    totalNeededQty: number;
  };
  quotesUsedPoolSize: number;
}

export interface StockVerificationUpdate {
  quoteId: string;
  availableQty: number;
  updatedUnitPriceUsd?: number;
}

export interface ReoptimizedCartOutcome {
  previousGrandTotalUsd: number;
  newGrandTotalUsd: number;
  totalDeltaUsd: number;
  affectedCards: {
    cardName: string;
    previousSellerName: string;
    newSellerNames: string[];
    reason: 'out_of_stock' | 'partial_stock' | 'price_changed';
    deltaUsd: number;
  }[];
  updatedResult: OptimizedDeckCartResult;
  updatedQuotes: VendorQuote[];
}

export type VendorQuoteProvider = (items: readonly CartDeckItem[]) => Promise<VendorQuote[]> | VendorQuote[];

function roundUsd(val: number): number {
  return Math.round((val + Number.EPSILON) * 100) / 100;
}

export function normalizeCardKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s*\/\/.*$/, '')
    .replace(/\s+/g, ' ');
}

export function isBasicLandCardName(name: string, typeLine?: string): boolean {
  if (typeLine && /\bbasic\s+(snow\s+)?land\b/i.test(typeLine)) return true;
  return BASIC_LAND_NAMES.has(normalizeCardKey(name));
}

/**
 * Converts a Playtable `ImportedDeckPayload` (or Deckstr deck shape) into normalized `CartDeckItem[]`.
 */
export function deckPayloadToCartItems(
  deck: {
    commanders?: { name: string; typeLine?: string; setCode?: string; collectorNumber?: string; foil?: boolean }[];
    companions?: { name: string; typeLine?: string; setCode?: string; collectorNumber?: string; foil?: boolean }[];
    mainboard?: {
      card: { name: string; typeLine?: string; setCode?: string; collectorNumber?: string; foil?: boolean };
      quantity: number;
    }[];
    sideboard?: {
      card: { name: string; typeLine?: string; setCode?: string; collectorNumber?: string; foil?: boolean };
      quantity: number;
    }[];
  },
  includeSideboard = true
): CartDeckItem[] {
  const merged = new Map<string, CartDeckItem>();

  const addEntry = (
    card: { name: string; typeLine?: string; setCode?: string; collectorNumber?: string; foil?: boolean },
    qty: number
  ) => {
    if (!card?.name || qty <= 0) return;
    const setKey = (card.setCode ?? '').toUpperCase();
    const cnKey = card.collectorNumber ?? '';
    const foilKey = card.foil ? '1' : '0';
    const key = `${normalizeCardKey(card.name)}|${setKey}|${cnKey}|${foilKey}`;
    const existing = merged.get(key);
    if (existing) {
      existing.quantity += qty;
    } else {
      merged.set(key, {
        cardName: card.name.trim(),
        quantity: qty,
        setCode: setKey || undefined,
        collectorNumber: cnKey || undefined,
        foil: Boolean(card.foil),
        isBasicLand: isBasicLandCardName(card.name, card.typeLine),
      });
    }
  };

  for (const cmd of deck.commanders ?? []) addEntry(cmd, 1);
  for (const comp of deck.companions ?? []) addEntry(comp, 1);
  for (const row of deck.mainboard ?? []) addEntry(row.card, row.quantity);
  if (includeSideboard) {
    for (const row of deck.sideboard ?? []) addEntry(row.card, row.quantity);
  }

  return [...merged.values()];
}

/**
 * Parses plain-text (`4 Sol Ring`, `1x Arcane Signet (CMM) 331`) or CSV (`Name,Quantity` / `Count,Name`)
 * owned-collection exports into a normalized card-name -> quantity map.
 */
export function parseOwnedCollectionText(rawText: string): Record<string, number> {
  const counts: Record<string, number> = {};
  if (!rawText || !rawText.trim()) return counts;

  const lines = rawText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return counts;

  // Detect simple CSV header
  const firstLower = lines[0].toLowerCase();
  if (firstLower.includes(',') && (firstLower.includes('name') || firstLower.includes('card'))) {
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const nameIdx = headers.findIndex((h) => h === 'name' || h === 'card name' || h === 'card');
    const qtyIdx = headers.findIndex((h) => h === 'quantity' || h === 'count' || h === 'qty' || h === 'tradelist count');
    if (nameIdx !== -1) {
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
        const name = cols[nameIdx];
        const qty = qtyIdx !== -1 ? Math.max(0, parseInt(cols[qtyIdx] ?? '1', 10) || 0) : 1;
        if (name && qty > 0) {
          const k = normalizeCardKey(name);
          counts[k] = (counts[k] ?? 0) + qty;
        }
      }
      return counts;
    }
  }

  for (const line of lines) {
    if (line.startsWith('//') || line.startsWith('#') || /^(deck|mainboard|sideboard|commander):?$/i.test(line)) {
      continue;
    }
    const m = line.match(/^(?:(\d+)\s*x?\s+)?(.+?)(?:\s*\([A-Za-z0-9]{2,6}\)(?:\s+[A-Za-z0-9★-]+)?)?(?:\s+\*F\*)?$/);
    if (!m) continue;
    const qty = m[1] ? parseInt(m[1], 10) : 1;
    const rawName = m[2].trim();
    if (!rawName || qty <= 0) continue;
    const k = normalizeCardKey(rawName);
    counts[k] = (counts[k] ?? 0) + qty;
  }

  return counts;
}

/**
 * Subtracts owned collection quantities and optionally removes basic lands before cart optimization.
 */
export function subtractOwnedCollection(
  items: readonly CartDeckItem[],
  options: {
    ownedText?: string;
    ownedCounts?: Record<string, number>;
    excludeBasicLands?: boolean;
  } = {}
): {
  neededItems: CartDeckItem[];
  totalOriginalQty: number;
  excludedBasicsQty: number;
  subtractedOwnedQty: number;
  totalNeededQty: number;
} {
  const pool: Record<string, number> = {};
  if (options.ownedText) {
    for (const [k, v] of Object.entries(parseOwnedCollectionText(options.ownedText))) {
      pool[k] = (pool[k] ?? 0) + v;
    }
  }
  if (options.ownedCounts) {
    for (const [k, v] of Object.entries(options.ownedCounts)) {
      const nk = normalizeCardKey(k);
      pool[nk] = (pool[nk] ?? 0) + Math.max(0, Math.floor(v));
    }
  }

  let totalOriginalQty = 0;
  let excludedBasicsQty = 0;
  let subtractedOwnedQty = 0;
  let totalNeededQty = 0;
  const neededItems: CartDeckItem[] = [];

  for (const item of items) {
    if (item.quantity <= 0) continue;
    totalOriginalQty += item.quantity;

    const isBasic = item.isBasicLand ?? isBasicLandCardName(item.cardName);
    if (options.excludeBasicLands && isBasic) {
      excludedBasicsQty += item.quantity;
      continue;
    }

    const nk = normalizeCardKey(item.cardName);
    const ownedAvail = pool[nk] ?? 0;
    const usedOwned = Math.min(item.quantity, ownedAvail);
    pool[nk] = ownedAvail - usedOwned;
    subtractedOwnedQty += usedOwned;

    const remaining = item.quantity - usedOwned;
    if (remaining > 0) {
      totalNeededQty += remaining;
      neededItems.push({
        ...item,
        quantity: remaining,
        isBasicLand: isBasic,
      });
    }
  }

  return {
    neededItems,
    totalOriginalQty,
    excludedBasicsQty,
    subtractedOwnedQty,
    totalNeededQty,
  };
}

function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

const KNOWN_CARD_ANCHOR_USD: Record<string, number> = {
  'sol ring': 1.85,
  'arcane signet': 0.95,
  'command tower': 0.45,
  'swords to plowshares': 1.35,
  'counterspell': 1.45,
  'lightning bolt': 1.95,
  'birds of paradise': 5.8,
  'rhystic study': 42.5,
  'smothering tithe': 26.0,
  'cyclonic rift': 31.5,
  'demonic tutor': 38.0,
  'esper sentinel': 29.0,
  'the one ring': 68.0,
  'orcish bowmasters': 46.0,
  'teferi\'s protection': 32.0,
  'atraxa, praetors\' voice': 19.5,
  'krenko, mob boss': 3.25,
};

/**
 * Generates deterministic, realistic multi-vendor quotes across printings, conditions (`NM`..`HP`),
 * finishes (`nonfoil` & `foil`), and stock counts for offline/sandbox operation and benchmarks,
 * while matching the exact `VendorQuote` contract returned by live Scryfall/MTGJSON/vendor adapters.
 */
export function seedVendorQuotesForItems(items: readonly CartDeckItem[]): VendorQuote[] {
  const quotes: VendorQuote[] = [];
  const seenKeys = new Set<string>();

  for (const item of items) {
    const norm = normalizeCardKey(item.cardName);
    const dedupKey = `${norm}|${(item.setCode ?? '').toUpperCase()}|${item.collectorNumber ?? ''}`;
    if (seenKeys.has(dedupKey)) continue;
    seenKeys.add(dedupKey);

    const h = fnv1a32(norm);
    const isBasic = item.isBasicLand ?? isBasicLandCardName(item.cardName);
    const anchor = isBasic
      ? 0.25
      : KNOWN_CARD_ANCHOR_USD[norm] ?? roundUsd(0.65 + ((h % 1800) / 100) * (((h >>> 8) % 5 === 0) ? 1.8 : 0.45));

    const reprintSet = 'CMM';
    const altSet = item.setCode ? item.setCode.toUpperCase() : ['MH3', '2X2', 'ELD', 'LTR', 'DMU'][h % 5];
    const cnBase = item.collectorNumber ?? String(40 + (h % 260));

    const sellers: {
      vendorId: VendorId;
      sellerId: string;
      sellerName: string;
      priceMult: number;
      baseShip?: number;
      perCardShip?: number;
      freeThresh?: number | null;
      stockBias: number;
    }[] = [
      {
        vendorId: 'tcgplayer_direct',
        sellerId: 'tcgplayer_direct',
        sellerName: 'TCGplayer Direct',
        priceMult: 1.03,
        stockBias: 16,
      },
      {
        vendorId: 'tcgplayer_market',
        sellerId: `tcg_mkt_${(h % 4) + 1}`,
        sellerName: ['Arcane Vault TCG', 'Lotus Horizon Games', 'TopDeck Singles', 'ManaForge Emporium'][h % 4],
        priceMult: 0.88 + ((h % 5) * 0.01),
        baseShip: 1.99,
        perCardShip: 0,
        freeThresh: null,
        stockBias: 8,
      },
      {
        vendorId: 'tcgplayer_market',
        sellerId: 'tcg_mkt_hub',
        sellerName: 'Emerald Superstore (TCG Market)',
        priceMult: 0.94,
        baseShip: 1.49,
        perCardShip: 0,
        freeThresh: 35.0,
        stockBias: 12,
      },
      {
        vendorId: 'card_kingdom',
        sellerId: 'card_kingdom',
        sellerName: 'Card Kingdom',
        priceMult: 1.09,
        stockBias: 20,
      },
      {
        vendorId: 'coolstuffinc',
        sellerId: 'coolstuffinc',
        sellerName: 'CoolStuffInc',
        priceMult: 0.98 + (((h >>> 4) % 6) * 0.01),
        stockBias: 12,
      },
      {
        vendorId: 'starcitygames',
        sellerId: 'starcitygames',
        sellerName: 'Star City Games',
        priceMult: 1.11,
        stockBias: 16,
      },
      {
        vendorId: 'cardmarket',
        sellerId: 'cardmarket_eu_hub',
        sellerName: 'Cardmarket EU PowerSeller',
        priceMult: 0.91,
        stockBias: 12,
      },
    ];

    for (const s of sellers) {
      const sellerHash = fnv1a32(`${norm}:${s.sellerId}`);
      const jitter = 0.96 + ((sellerHash % 9) * 0.01);
      const nmBase = Math.max(0.15, roundUsd(anchor * s.priceMult * jitter));
      const stockQty = Math.max(4, s.stockBias + (sellerHash % 8));

      // Cheap reprint NM + LP + MP/HP
      quotes.push({
        quoteId: `${s.sellerId}:${norm}:${reprintSet}:NM:nf`,
        vendorId: s.vendorId,
        sellerId: s.sellerId,
        sellerName: s.sellerName,
        cardName: item.cardName,
        setCode: reprintSet,
        collectorNumber: '901',
        condition: 'NM',
        foil: false,
        unitPriceUsd: nmBase,
        stockQty,
        sellerBaseShippingUsd: s.baseShip,
        sellerPerCardShippingUsd: s.perCardShip,
        sellerFreeShippingThresholdUsd: s.freeThresh,
      });

      quotes.push({
        quoteId: `${s.sellerId}:${norm}:${reprintSet}:LP:nf`,
        vendorId: s.vendorId,
        sellerId: s.sellerId,
        sellerName: s.sellerName,
        cardName: item.cardName,
        setCode: reprintSet,
        collectorNumber: '901',
        condition: 'LP',
        foil: false,
        unitPriceUsd: Math.max(0.12, roundUsd(nmBase * 0.91)),
        stockQty,
        sellerBaseShippingUsd: s.baseShip,
        sellerPerCardShippingUsd: s.perCardShip,
        sellerFreeShippingThresholdUsd: s.freeThresh,
      });

      quotes.push({
        quoteId: `${s.sellerId}:${norm}:${reprintSet}:HP:nf`,
        vendorId: s.vendorId,
        sellerId: s.sellerId,
        sellerName: s.sellerName,
        cardName: item.cardName,
        setCode: reprintSet,
        collectorNumber: '901',
        condition: 'HP',
        foil: false,
        unitPriceUsd: Math.max(0.1, roundUsd(nmBase * 0.74)),
        stockQty: Math.max(2, Math.floor(stockQty / 2)),
        sellerBaseShippingUsd: s.baseShip,
        sellerPerCardShippingUsd: s.perCardShip,
        sellerFreeShippingThresholdUsd: s.freeThresh,
      });

      // Foil variant
      quotes.push({
        quoteId: `${s.sellerId}:${norm}:${reprintSet}:NM:foil`,
        vendorId: s.vendorId,
        sellerId: s.sellerId,
        sellerName: s.sellerName,
        cardName: item.cardName,
        setCode: reprintSet,
        collectorNumber: '901',
        condition: 'NM',
        foil: true,
        unitPriceUsd: roundUsd(nmBase * 1.45 + 0.35),
        stockQty: Math.max(2, Math.floor(stockQty / 2)),
        sellerBaseShippingUsd: s.baseShip,
        sellerPerCardShippingUsd: s.perCardShip,
        sellerFreeShippingThresholdUsd: s.freeThresh,
      });

      // Exact / collector printing (slightly pricier than budget reprint)
      if (altSet !== reprintSet || cnBase !== '901') {
        quotes.push({
          quoteId: `${s.sellerId}:${norm}:${altSet}:${cnBase}:NM:nf`,
          vendorId: s.vendorId,
          sellerId: s.sellerId,
          sellerName: s.sellerName,
          cardName: item.cardName,
          setCode: altSet,
          collectorNumber: cnBase,
          condition: 'NM',
          foil: false,
          unitPriceUsd: roundUsd(nmBase * 1.28 + 0.25),
          stockQty: Math.max(4, stockQty - 2),
          sellerBaseShippingUsd: s.baseShip,
          sellerPerCardShippingUsd: s.perCardShip,
          sellerFreeShippingThresholdUsd: s.freeThresh,
        });

        quotes.push({
          quoteId: `${s.sellerId}:${norm}:${altSet}:${cnBase}:NM:foil`,
          vendorId: s.vendorId,
          sellerId: s.sellerId,
          sellerName: s.sellerName,
          cardName: item.cardName,
          setCode: altSet,
          collectorNumber: cnBase,
          condition: 'NM',
          foil: true,
          unitPriceUsd: roundUsd(nmBase * 1.75 + 0.5),
          stockQty: Math.max(2, Math.floor(stockQty / 2)),
          sellerBaseShippingUsd: s.baseShip,
          sellerPerCardShippingUsd: s.perCardShip,
          sellerFreeShippingThresholdUsd: s.freeThresh,
        });
      }
    }
  }

  return quotes;
}

function getSellerShippingRule(sampleQuote: VendorQuote): {
  baseUsd: number;
  perCardUsd: number;
  freeThresholdUsd: number | null;
  schedule: VendorShippingSchedule;
} {
  const schedule = VENDOR_SHIPPING_SCHEDULES[sampleQuote.vendorId];
  return {
    baseUsd: sampleQuote.sellerBaseShippingUsd ?? schedule.baseShippingUsd,
    perCardUsd: sampleQuote.sellerPerCardShippingUsd ?? schedule.perCardShippingUsd,
    freeThresholdUsd:
      sampleQuote.sellerFreeShippingThresholdUsd !== undefined
        ? sampleQuote.sellerFreeShippingThresholdUsd
        : schedule.freeShippingThresholdUsd,
    schedule,
  };
}

export function computeSellerShippingUsd(
  sampleQuote: VendorQuote,
  subtotalUsd: number,
  totalCardQty: number
): {
  shippingUsd: number;
  freeShippingThresholdUsd: number | null;
  freeShippingUnlocked: boolean;
  amountToFreeShippingUsd: number | null;
} {
  if (totalCardQty <= 0) {
    return {
      shippingUsd: 0,
      freeShippingThresholdUsd: null,
      freeShippingUnlocked: false,
      amountToFreeShippingUsd: null,
    };
  }
  const rule = getSellerShippingRule(sampleQuote);
  if (rule.freeThresholdUsd !== null && subtotalUsd + 1e-6 >= rule.freeThresholdUsd) {
    return {
      shippingUsd: 0,
      freeShippingThresholdUsd: rule.freeThresholdUsd,
      freeShippingUnlocked: true,
      amountToFreeShippingUsd: 0,
    };
  }
  const rawShip = roundUsd(rule.baseUsd + rule.perCardUsd * totalCardQty);
  return {
    shippingUsd: rawShip,
    freeShippingThresholdUsd: rule.freeThresholdUsd,
    freeShippingUnlocked: false,
    amountToFreeShippingUsd:
      rule.freeThresholdUsd !== null ? roundUsd(Math.max(0, rule.freeThresholdUsd - subtotalUsd)) : null,
  };
}

interface FilteredItemQuotes {
  item: CartDeckItem;
  eligibleQuotes: (VendorQuote & { printingFallbackUsed?: boolean })[];
}

function filterQuotesForNeededItems(
  neededItems: readonly CartDeckItem[],
  quotes: readonly VendorQuote[],
  prefs: CartOptimizationPreferences
): FilteredItemQuotes[] {
  const allowedVendorSet =
    prefs.allowedVendors && prefs.allowedVendors.length > 0 ? new Set(prefs.allowedVendors) : null;
  const minRank = CONDITION_RANK[prefs.minCondition];

  const byNormName = new Map<string, VendorQuote[]>();
  for (const q of quotes) {
    if (q.stockQty <= 0) continue;
    if (allowedVendorSet && !allowedVendorSet.has(q.vendorId)) continue;
    if (CONDITION_RANK[q.condition] < minRank) continue;
    if (prefs.finishPreference === 'foil' && !q.foil) continue;
    if (prefs.finishPreference === 'nonfoil' && q.foil) continue;

    const k = normalizeCardKey(q.cardName);
    const arr = byNormName.get(k);
    if (arr) arr.push(q);
    else byNormName.set(k, [q]);
  }

  return neededItems.map((item) => {
    const candidates = byNormName.get(normalizeCardKey(item.cardName)) ?? [];
    let selected: (VendorQuote & { printingFallbackUsed?: boolean })[] = candidates;

    if (prefs.printingPreference === 'exact_printing' && item.setCode) {
      const targetSet = item.setCode.toUpperCase();
      const exact = candidates.filter(
        (q) =>
          q.setCode.toUpperCase() === targetSet &&
          (!item.collectorNumber || q.collectorNumber === item.collectorNumber)
      );
      if (exact.length > 0 || prefs.strictExactPrinting) {
        selected = exact;
      } else {
        selected = candidates.map((q) => ({ ...q, printingFallbackUsed: true }));
      }
    }

    const sorted = [...selected].sort((a, b) => a.unitPriceUsd - b.unitPriceUsd || b.stockQty - a.stockQty);
    return { item, eligibleQuotes: sorted };
  });
}

interface EvaluatedPlan {
  packages: VendorPackagePlan[];
  packageCount: number;
  cardsSubtotalUsd: number;
  shippingTotalUsd: number;
  grandTotalUsd: number;
  unfulfilled: UnfulfilledCartItem[];
  unfulfilledQty: number;
  activeSellerIds: Set<string>;
}

/**
 * Given a restriction (or preference priority) of allowed `sellerIds` — plus optional fallback to all sellers
 * only when the restricted set lacks stock — allocates each needed card's copies to minimize marginal cost.
 */
function buildPlanForSellerSet(
  filteredItems: readonly FilteredItemQuotes[],
  preferredSellerIds: ReadonlySet<string> | null,
  allowFallbackOutsideSet: boolean,
  prefs: CartOptimizationPreferences
): EvaluatedPlan {
  // Track remaining stock per quoteId so multiple entries for the same card never over-allocate stock
  const remainingStock = new Map<string, number>();
  const sampleQuoteBySeller = new Map<string, VendorQuote>();
  const linesBySeller = new Map<string, AllocatedCartLine[]>();
  const unfulfilled: UnfulfilledCartItem[] = [];
  let unfulfilledQty = 0;

  for (const fi of filteredItems) {
    for (const q of fi.eligibleQuotes) {
      if (!remainingStock.has(q.quoteId)) {
        remainingStock.set(q.quoteId, q.stockQty);
      }
      if (!sampleQuoteBySeller.has(q.sellerId)) {
        sampleQuoteBySeller.set(q.sellerId, q);
      }
    }
  }

  for (const { item, eligibleQuotes } of filteredItems) {
    let needed = item.quantity;

    const primaryPool = preferredSellerIds
      ? eligibleQuotes.filter((q) => preferredSellerIds.has(q.sellerId))
      : eligibleQuotes;

    const allocateFromPool = (pool: (VendorQuote & { printingFallbackUsed?: boolean })[]) => {
      for (const q of pool) {
        if (needed <= 0) break;
        const avail = remainingStock.get(q.quoteId) ?? 0;
        if (avail <= 0) continue;
        const take = Math.min(needed, avail);
        remainingStock.set(q.quoteId, avail - take);
        needed -= take;

        const arr = linesBySeller.get(q.sellerId) ?? [];
        const existingLine = arr.find((l) => l.quoteId === q.quoteId);
        if (existingLine) {
          existingLine.quantity += take;
          existingLine.lineTotalUsd = roundUsd(existingLine.quantity * existingLine.unitPriceUsd);
        } else {
          arr.push({
            quoteId: q.quoteId,
            cardName: q.cardName,
            setCode: q.setCode,
            collectorNumber: q.collectorNumber,
            condition: q.condition,
            foil: q.foil,
            quantity: take,
            unitPriceUsd: q.unitPriceUsd,
            lineTotalUsd: roundUsd(take * q.unitPriceUsd),
            printingFallbackUsed: q.printingFallbackUsed,
          });
        }
        linesBySeller.set(q.sellerId, arr);
      }
    };

    allocateFromPool(primaryPool);
    if (needed > 0 && preferredSellerIds && allowFallbackOutsideSet) {
      const secondaryPool = eligibleQuotes.filter((q) => !preferredSellerIds.has(q.sellerId));
      allocateFromPool(secondaryPool);
    }

    if (needed > 0) {
      unfulfilledQty += needed;
      unfulfilled.push({
        cardName: item.cardName,
        missingQty: needed,
        reason:
          eligibleQuotes.length === 0
            ? `No in-stock listing matching ${prefs.minCondition}+ / ${prefs.finishPreference}`
            : 'Insufficient combined vendor stock',
      });
    }
  }

  return finalizeSellerLines(linesBySeller, sampleQuoteBySeller, unfulfilled, unfulfilledQty, prefs);
}

function finalizeSellerLines(
  linesBySeller: Map<string, AllocatedCartLine[]>,
  sampleQuoteBySeller: Map<string, VendorQuote>,
  unfulfilled: UnfulfilledCartItem[],
  unfulfilledQty: number,
  prefs: CartOptimizationPreferences
): EvaluatedPlan {
  const packages: VendorPackagePlan[] = [];
  let cardsSubtotalUsd = 0;
  let shippingTotalUsd = 0;
  const activeSellerIds = new Set<string>();

  for (const [sellerId, items] of linesBySeller.entries()) {
    if (items.length === 0) continue;
    const sample = sampleQuoteBySeller.get(sellerId)!;
    const schedule = VENDOR_SHIPPING_SCHEDULES[sample.vendorId];
    const subtotalUsd = roundUsd(items.reduce((acc, l) => acc + l.lineTotalUsd, 0));
    const totalCardQty = items.reduce((acc, l) => acc + l.quantity, 0);
    const ship = computeSellerShippingUsd(sample, subtotalUsd, totalCardQty);
    const totalUsd = roundUsd(subtotalUsd + ship.shippingUsd);

    const massEntryText = items
      .map((l) => `${l.quantity} ${l.cardName} [${l.setCode}] ${l.collectorNumber}${l.foil ? ' *F*' : ''}`)
      .join('\n');

    const condLabel = `Min Condition: ${prefs.minCondition}${prefs.finishPreference !== 'any' ? ` (${prefs.finishPreference})` : ''}`;
    const shipBadge = ship.freeShippingUnlocked
      ? `Free shipping unlocked ($${ship.freeShippingThresholdUsd?.toFixed(2)}+ threshold met)`
      : ship.amountToFreeShippingUsd !== null
        ? `$${ship.shippingUsd.toFixed(2)} est. shipping ($${ship.amountToFreeShippingUsd.toFixed(2)} away from free shipping)`
        : `$${ship.shippingUsd.toFixed(2)} est. shipping`;

    packages.push({
      sellerId,
      sellerName: sample.sellerName,
      vendorId: sample.vendorId,
      vendorLabel: schedule.vendorLabel,
      region: schedule.region,
      items: items.sort((a, b) => b.lineTotalUsd - a.lineTotalUsd || a.cardName.localeCompare(b.cardName)),
      totalCardQty,
      subtotalUsd,
      shippingUsd: ship.shippingUsd,
      freeShippingThresholdUsd: ship.freeShippingThresholdUsd,
      freeShippingUnlocked: ship.freeShippingUnlocked,
      amountToFreeShippingUsd: ship.amountToFreeShippingUsd,
      totalUsd,
      checkoutUrl: schedule.checkoutUrl,
      massEntryText,
      stepChecklist: [
        `Copy ${totalCardQty}-card mass-entry list for ${sample.sellerName}`,
        `Open ${schedule.vendorLabel} Deck Builder / Mass Entry`,
        `Set filters to ${condLabel} — ${shipBadge}`,
        `Verify cart total (~$${totalUsd.toFixed(2)}) before checkout`,
      ],
    });

    cardsSubtotalUsd = roundUsd(cardsSubtotalUsd + subtotalUsd);
    shippingTotalUsd = roundUsd(shippingTotalUsd + ship.shippingUsd);
    activeSellerIds.add(sellerId);
  }

  packages.sort((a, b) => b.totalUsd - a.totalUsd || a.sellerId.localeCompare(b.sellerId));

  return {
    packages,
    packageCount: packages.length,
    cardsSubtotalUsd,
    shippingTotalUsd,
    grandTotalUsd: roundUsd(cardsSubtotalUsd + shippingTotalUsd),
    unfulfilled,
    unfulfilledQty,
    activeSellerIds,
  };
}

function computePlanObjectiveScore(plan: EvaluatedPlan, prefs: CartOptimizationPreferences): number {
  const pkgPenalty =
    prefs.mode === 'cheapest_total'
      ? 0
      : prefs.mode === 'fewest_packages'
        ? 250.0
        : prefs.balancedPackagePenaltyUsd ?? 2.25;
  const extraPackages = Math.max(0, plan.packageCount - 1);
  return plan.unfulfilledQty * 10000 + plan.grandTotalUsd + extraPackages * pkgPenalty;
}

/**
 * Attempts a free-shipping threshold push on a multi-seller plan:
 * if an active seller with a free-shipping threshold (e.g. $50 Direct) is close to threshold,
 * shifts cards with the smallest unit-price premium from other packages into that target seller
 * when unlocking $0 shipping reduces the overall objective score.
 */
function tryFreeShippingThresholdPush(
  filteredItems: readonly FilteredItemQuotes[],
  activeSellerIds: ReadonlySet<string>,
  targetSellerId: string,
  prefs: CartOptimizationPreferences
): EvaluatedPlan | null {
  // Check if targetSeller has a free-shipping threshold
  let targetSample: VendorQuote | null = null;
  for (const fi of filteredItems) {
    const hit = fi.eligibleQuotes.find((q) => q.sellerId === targetSellerId);
    if (hit) {
      targetSample = hit;
      break;
    }
  }
  if (!targetSample) return null;
  const rule = getSellerShippingRule(targetSample);
  if (rule.freeThresholdUsd === null) return null;

  // Score each item by unit-price delta between targetSeller and best other active seller
  const itemEntries = filteredItems.map((fi, idx) => {
    const targetQuote = fi.eligibleQuotes.find((q) => q.sellerId === targetSellerId && q.stockQty >= fi.item.quantity);
    const otherQuote = fi.eligibleQuotes.find(
      (q) => activeSellerIds.has(q.sellerId) && q.sellerId !== targetSellerId
    );
    const premiumPerCopy =
      targetQuote && otherQuote ? targetQuote.unitPriceUsd - otherQuote.unitPriceUsd : targetQuote ? 0 : 9999;
    return { idx, fi, targetQuote, otherQuote, premiumPerCopy };
  });

  // Start with items where targetSeller is already cheapest, then add lowest-premium items until threshold is crossed
  itemEntries.sort((a, b) => a.premiumPerCopy - b.premiumPerCopy);

  let runningTargetSubtotal = 0;
  const forcedToTarget = new Set<number>();
  for (const entry of itemEntries) {
    if (!entry.targetQuote) continue;
    if (entry.premiumPerCopy <= 0 || runningTargetSubtotal < rule.freeThresholdUsd) {
      forcedToTarget.add(entry.idx);
      runningTargetSubtotal += entry.targetQuote.unitPriceUsd * entry.fi.item.quantity;
    }
  }

  if (runningTargetSubtotal + 1e-6 < rule.freeThresholdUsd) return null;

  // Build customized allocation where forcedToTarget items pick targetSeller first
  const customFiltered: FilteredItemQuotes[] = filteredItems.map((fi, idx) => {
    if (!forcedToTarget.has(idx)) return fi;
    const targetOnly = fi.eligibleQuotes.filter((q) => q.sellerId === targetSellerId);
    const rest = fi.eligibleQuotes.filter((q) => q.sellerId !== targetSellerId);
    return {
      item: fi.item,
      eligibleQuotes: [...targetOnly, ...rest],
    };
  });

  return buildPlanForSellerSet(customFiltered, activeSellerIds, true, prefs);
}

/**
 * Core shipping-aware multi-vendor deck cart optimizer.
 * Solves across single-seller baselines, naive cheapest-per-card, seller-drop subset consolidation,
 * seller-addition local search, and free-shipping threshold lookahead.
 */
export function optimizeDeckCart(params: {
  items: readonly CartDeckItem[];
  quotes?: readonly VendorQuote[];
  ownedCollectionText?: string;
  ownedCollectionCounts?: Record<string, number>;
  preferences?: Partial<CartOptimizationPreferences>;
}): OptimizedDeckCartResult {
  const prefs: CartOptimizationPreferences = {
    ...DEFAULT_CART_PREFERENCES,
    ...params.preferences,
  };

  const subtraction = subtractOwnedCollection(params.items, {
    ownedText: params.ownedCollectionText,
    ownedCounts: params.ownedCollectionCounts,
    excludeBasicLands: prefs.excludeBasicLands ?? false,
  });

  if (subtraction.neededItems.length === 0) {
    return {
      mode: prefs.mode,
      preferences: prefs,
      packages: [],
      packageCount: 0,
      cardsSubtotalUsd: 0,
      shippingTotalUsd: 0,
      grandTotalUsd: 0,
      naivePerCardTotalUsd: 0,
      naivePackageCount: 0,
      bestSingleSellerTotalUsd: 0,
      savingsVsNaiveUsd: 0,
      savingsVsSingleSellerUsd: 0,
      unfulfilled: [],
      collectionSummary: {
        totalOriginalQty: subtraction.totalOriginalQty,
        excludedBasicsQty: subtraction.excludedBasicsQty,
        subtractedOwnedQty: subtraction.subtractedOwnedQty,
        totalNeededQty: 0,
      },
      quotesUsedPoolSize: 0,
    };
  }

  const quotePool = params.quotes ?? seedVendorQuotesForItems(subtraction.neededItems);
  const filtered = filterQuotesForNeededItems(subtraction.neededItems, quotePool, prefs);

  // 1. Naive cheapest-per-card baseline (all sellers eligible simultaneously -> greedy lowest unitPriceUsd per card)
  const naivePlan = buildPlanForSellerSet(filtered, null, true, prefs);

  // Gather all distinct sellerIds appearing in eligible quotes
  const sellerCoverageCount = new Map<string, number>();
  for (const fi of filtered) {
    const seenForCard = new Set<string>();
    for (const q of fi.eligibleQuotes) {
      if (!seenForCard.has(q.sellerId)) {
        seenForCard.add(q.sellerId);
        sellerCoverageCount.set(q.sellerId, (sellerCoverageCount.get(q.sellerId) ?? 0) + fi.item.quantity);
      }
    }
  }

  const allSellerIds = [...sellerCoverageCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([sid]) => sid);

  let bestPlan = naivePlan;
  let bestScore = computePlanObjectiveScore(naivePlan, prefs);

  const consider = (candidate: EvaluatedPlan | null) => {
    if (!candidate) return;
    const sc = computePlanObjectiveScore(candidate, prefs);
    if (
      sc < bestScore - 1e-6 ||
      (Math.abs(sc - bestScore) <= 1e-6 && candidate.grandTotalUsd < bestPlan.grandTotalUsd - 1e-6)
    ) {
      bestPlan = candidate;
      bestScore = sc;
    }
  };

  // 2. Evaluate all Single-Seller baselines (plus minimal fallback if single seller lacks stock on 1-2 cards)
  let bestSingleSellerTotalUsd: number | null = null;
  for (const sid of allSellerIds) {
    const singleStrict = buildPlanForSellerSet(filtered, new Set([sid]), false, prefs);
    if (singleStrict.unfulfilledQty === 0) {
      if (bestSingleSellerTotalUsd === null || singleStrict.grandTotalUsd < bestSingleSellerTotalUsd) {
        bestSingleSellerTotalUsd = singleStrict.grandTotalUsd;
      }
    }
    const singleWithFallback =
      singleStrict.unfulfilledQty === 0
        ? singleStrict
        : buildPlanForSellerSet(filtered, new Set([sid]), true, prefs);
    consider(singleWithFallback);
  }

  // 3. Evaluate top 2-seller hub pairs
  const topHubs = allSellerIds.slice(0, Math.min(8, allSellerIds.length));
  for (let i = 0; i < topHubs.length; i++) {
    for (let j = i + 1; j < topHubs.length; j++) {
      const pairSet = new Set([topHubs[i], topHubs[j]]);
      const pairPlan = buildPlanForSellerSet(filtered, pairSet, true, prefs);
      consider(pairPlan);
      // Also test free-shipping threshold push on each hub in the pair
      consider(tryFreeShippingThresholdPush(filtered, pairSet, topHubs[i], prefs));
      consider(tryFreeShippingThresholdPush(filtered, pairSet, topHubs[j], prefs));
    }
  }

  // 4. Iterative Seller-Drop (Package Consolidation) & Seller-Swap Local Search starting from Naive Plan
  let currentSet = new Set(naivePlan.activeSellerIds);
  let improved = true;
  let guard = 0;
  while (improved && currentSet.size > 1 && guard < 32) {
    improved = false;
    guard++;
    let stepBestSet: Set<string> | null = null;
    let stepBestPlan: EvaluatedPlan | null = null;
    let stepBestScore = computePlanObjectiveScore(
      buildPlanForSellerSet(filtered, currentSet, true, prefs),
      prefs
    );

    for (const dropSeller of currentSet) {
      const nextSet = new Set(currentSet);
      nextSet.delete(dropSeller);
      const cand = buildPlanForSellerSet(filtered, nextSet, true, prefs);
      const sc = computePlanObjectiveScore(cand, prefs);
      consider(cand);
      if (sc < stepBestScore - 1e-6) {
        stepBestScore = sc;
        stepBestSet = nextSet;
        stepBestPlan = cand;
      }
    }

    if (stepBestSet && stepBestPlan) {
      currentSet = stepBestSet;
      improved = true;
      for (const sid of currentSet) {
        consider(tryFreeShippingThresholdPush(filtered, currentSet, sid, prefs));
      }
    }
  }

  // 5. Also run greedy Seller-Addition from best single seller (adds a 2nd/3rd seller only when net savings > shipping!)
  if (topHubs.length > 0) {
    let growSet = new Set([ bestPlan.activeSellerIds.values().next().value ?? topHubs[0] ]);
    for (let step = 0; step < 3; step++) {
      let addedSeller: string | null = null;
      for (const candSid of allSellerIds) {
        if (growSet.has(candSid)) continue;
        const testSet = new Set(growSet);
        testSet.add(candSid);
        const plan = buildPlanForSellerSet(filtered, testSet, true, prefs);
        const beforeScore = bestScore;
        consider(plan);
        consider(tryFreeShippingThresholdPush(filtered, testSet, candSid, prefs));
        if (bestScore < beforeScore - 1e-6) {
          addedSeller = candSid;
        }
      }
      if (addedSeller) {
        growSet.add(addedSeller);
      } else {
        break;
      }
    }
  }

  // Also test threshold push on all active sellers of the naive & best plan
  for (const sid of [...bestPlan.activeSellerIds]) {
    consider(tryFreeShippingThresholdPush(filtered, bestPlan.activeSellerIds, sid, prefs));
  }

  const savingsVsNaiveUsd = roundUsd(Math.max(0, naivePlan.grandTotalUsd - bestPlan.grandTotalUsd));
  const savingsVsSingleSellerUsd =
    bestSingleSellerTotalUsd !== null
      ? roundUsd(Math.max(0, bestSingleSellerTotalUsd - bestPlan.grandTotalUsd))
      : 0;

  return {
    mode: prefs.mode,
    preferences: prefs,
    packages: bestPlan.packages,
    packageCount: bestPlan.packageCount,
    cardsSubtotalUsd: bestPlan.cardsSubtotalUsd,
    shippingTotalUsd: bestPlan.shippingTotalUsd,
    grandTotalUsd: bestPlan.grandTotalUsd,
    naivePerCardTotalUsd: naivePlan.grandTotalUsd,
    naivePackageCount: naivePlan.packageCount,
    bestSingleSellerTotalUsd,
    savingsVsNaiveUsd,
    savingsVsSingleSellerUsd,
    unfulfilled: bestPlan.unfulfilled,
    collectionSummary: {
      totalOriginalQty: subtraction.totalOriginalQty,
      excludedBasicsQty: subtraction.excludedBasicsQty,
      subtractedOwnedQty: subtraction.subtractedOwnedQty,
      totalNeededQty: subtraction.totalNeededQty,
    },
    quotesUsedPoolSize: quotePool.length,
  };
}

/**
 *Async wrapper supporting injectable live vendor/Scryfall quote providers (`VendorQuoteProvider`).
 */
export async function optimizeDeckCartWithProvider(params: {
  items: readonly CartDeckItem[];
  quoteProvider: VendorQuoteProvider;
  ownedCollectionText?: string;
  ownedCollectionCounts?: Record<string, number>;
  preferences?: Partial<CartOptimizationPreferences>;
}): Promise<OptimizedDeckCartResult> {
  const quotes = await params.quoteProvider(params.items);
  return optimizeDeckCart({
    items: params.items,
    quotes,
    ownedCollectionText: params.ownedCollectionText,
    ownedCollectionCounts: params.ownedCollectionCounts,
    preferences: params.preferences,
  });
}

/**
 * Applies live stock/price verification updates (e.g. an item sold out or changed price right before checkout)
 * and immediately re-optimizes the entire deck cart across remaining in-stock listings.
 */
export function reoptimizeAfterStockCheck(params: {
  items: readonly CartDeckItem[];
  quotes?: readonly VendorQuote[];
  previousResult: OptimizedDeckCartResult;
  stockUpdates: readonly StockVerificationUpdate[];
  ownedCollectionText?: string;
  ownedCollectionCounts?: Record<string, number>;
  preferences?: Partial<CartOptimizationPreferences>;
}): ReoptimizedCartOutcome {
  const baseQuotes = params.quotes ?? seedVendorQuotesForItems(params.items);
  const updateMap = new Map<string, StockVerificationUpdate>();
  for (const u of params.stockUpdates) {
    updateMap.set(u.quoteId, u);
  }

  const updatedQuotes = baseQuotes.map((q) => {
    const upd = updateMap.get(q.quoteId);
    if (!upd) return q;
    return {
      ...q,
      stockQty: Math.max(0, Math.floor(upd.availableQty)),
      unitPriceUsd:
        upd.updatedUnitPriceUsd !== undefined ? roundUsd(Math.max(0.01, upd.updatedUnitPriceUsd)) : q.unitPriceUsd,
    };
  });

  const prefs = params.preferences ?? params.previousResult.preferences;
  const updatedResult = optimizeDeckCart({
    items: params.items,
    quotes: updatedQuotes,
    ownedCollectionText: params.ownedCollectionText,
    ownedCollectionCounts: params.ownedCollectionCounts,
    preferences: prefs,
  });

  // Compute affected card delta report
  const affectedCards: ReoptimizedCartOutcome['affectedCards'] = [];
  for (const pkg of params.previousResult.packages) {
    for (const line of pkg.items) {
      const upd = updateMap.get(line.quoteId);
      if (!upd) continue;
      const reason: 'out_of_stock' | 'partial_stock' | 'price_changed' | null =
        upd.availableQty <= 0
          ? 'out_of_stock'
          : upd.availableQty < line.quantity
            ? 'partial_stock'
            : upd.updatedUnitPriceUsd !== undefined && Math.abs(upd.updatedUnitPriceUsd - line.unitPriceUsd) >= 0.01
              ? 'price_changed'
              : null;
      if (!reason) continue;

      const norm = normalizeCardKey(line.cardName);
      const newLines: { sellerName: string; lineTotal: number }[] = [];
      for (const newPkg of updatedResult.packages) {
        for (const nl of newPkg.items) {
          if (normalizeCardKey(nl.cardName) === norm) {
            newLines.push({ sellerName: newPkg.sellerName, lineTotal: nl.lineTotalUsd });
          }
        }
      }
      const newCardTotal = newLines.reduce((a, b) => a + b.lineTotal, 0);
      affectedCards.push({
        cardName: line.cardName,
        previousSellerName: pkg.sellerName,
        newSellerNames: [...new Set(newLines.map((n) => n.sellerName))],
        reason,
        deltaUsd: roundUsd(newCardTotal - line.lineTotalUsd),
      });
    }
  }

  return {
    previousGrandTotalUsd: params.previousResult.grandTotalUsd,
    newGrandTotalUsd: updatedResult.grandTotalUsd,
    totalDeltaUsd: roundUsd(updatedResult.grandTotalUsd - params.previousResult.grandTotalUsd),
    affectedCards,
    updatedResult,
    updatedQuotes,
  };
}

/**
 * Helper for 1-click "Verify Live Stock" button when testing in offline/sandbox mode:
 * flags the top-value allocated quote as out-of-stock so the user can inspect real-time fallback routing.
 */
export function buildSimulatedStockCheckUpdates(result: OptimizedDeckCartResult): StockVerificationUpdate[] {
  for (const pkg of result.packages) {
    if (pkg.items.length > 0) {
      return [{ quoteId: pkg.items[0].quoteId, availableQty: 0 }];
    }
  }
  return [];
}
